export const getBridgeBaseUrl = (): string => {
  if (typeof window !== "undefined") {
    const custom = window.localStorage?.getItem("pos_print_bridge_url");
    if (custom) return custom;
  }
  return process.env.REACT_APP_PRINT_BRIDGE_URL || "http://127.0.0.1:9100";
};

export const getBridgeToken = (): string => {
  if (typeof window !== "undefined") {
    const custom = window.localStorage?.getItem("pos_print_bridge_token");
    if (custom) return custom;
  }
  return process.env.REACT_APP_PRINT_BRIDGE_TOKEN || "1markaz-pos-bridge-token";
};

export interface BridgeHealthResponse {
  status: string;
  service: string;
  version: string;
  printer: string | null;
  uptimeSeconds: number;
}

export interface BridgePrintResponse {
  ok: boolean;
  printer?: string;
  bytesWritten?: number;
  error?: string;
}

/**
 * Fast health check to see if the local print bridge service is running.
 */
export const checkBridgeHealth = async (timeoutMs = 800): Promise<BridgeHealthResponse | null> => {
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    const res = await fetch(`${getBridgeBaseUrl()}/health`, {
      method: "GET",
      headers: {
        "X-Bridge-Token": getBridgeToken(),
      },
      signal: controller.signal,
    });
    clearTimeout(timer);
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
};

/**
 * Send raw ESC/POS byte array directly to the local print bridge.
 */
export const sendRawToBridge = async (
  bytes: Uint8Array,
  timeoutMs = 4000
): Promise<BridgePrintResponse> => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(`${getBridgeBaseUrl()}/print`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Bridge-Token": getBridgeToken(),
      },
      body: JSON.stringify({ bytes: Array.from(bytes) }),
      signal: controller.signal,
    });
    clearTimeout(timer);

    const data: BridgePrintResponse = await res.json().catch(() => ({
      ok: false,
      error: `HTTP ${res.status}`,
    }));

    if (!res.ok || !data.ok) {
      throw new Error(data.error || `Print bridge error (HTTP ${res.status})`);
    }

    return data;
  } catch (err) {
    clearTimeout(timer);
    throw err;
  }
};

/**
 * Send cash drawer trigger pulse to local print bridge.
 */
export const openDrawerViaBridge = async (timeoutMs = 2000): Promise<boolean> => {
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    const res = await fetch(`${getBridgeBaseUrl()}/open-drawer`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Bridge-Token": getBridgeToken(),
      },
      signal: controller.signal,
    });
    clearTimeout(timer);
    return res.ok;
  } catch {
    return false;
  }
};
